import { Html5Qrcode } from 'html5-qrcode';
import {
  Flashlight,
  FlashlightOff,
  Minus,
  Plus,
  SwitchCameraIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../Button';
import { Slider } from '../Slider';
import { Spinner } from '../Spinner';

type QRCodeScannerProps = {
  onSuccess?: (code: string) => void;
};

type CameraFacingMode = 'user' | 'environment';

const QRCodeScanner = ({ onSuccess }: QRCodeScannerProps) => {
  const qrRegionId = 'qr-reader';
  const qrCodeScannerRef = useRef<Html5Qrcode | null>(null);
  const [trackCapabilities, setTrackCapabilities] = useState<
    MediaTrackCapabilities & {
      zoom?: { max: number; min: number; step: number };
      torch?: boolean;
    }
  >();
  const [isScanning, setIsScanning] = useState(false);
  const [facingMode, setFacingMode] = useState<CameraFacingMode>('environment');
  const [mediaTrackConstraints, setMediaTrackConstraints] = useState<
    MediaTrackConstraints & { torch?: boolean }
  >({
    focusMode: 'continuous',
  } as MediaTrackConstraints);
  const lastErrorTime = useRef<number>(0);

  const config = {
    fps: 10,
    qrbox: function (viewfinderWidth: number, viewfinderHeight: number) {
      const minEdgePercentage = 0.8;
      const calcMinEdgeSize = Math.floor(
        Math.min(viewfinderWidth, viewfinderHeight) * minEdgePercentage,
      );

      return { width: calcMinEdgeSize, height: calcMinEdgeSize };
    },
  };

  const qrCodeSuccessCallback = (decodedText: string) => {
    console.log('QR code detected:', decodedText);
    if (onSuccess) {
      onSuccess(decodedText);
    }
  };

  const qrCodeErrorCallback = (errorMessage: string) => {
    const now = Date.now();
    if (now - lastErrorTime.current > 1000) {
      console.log('QR scan failed:', errorMessage);
      lastErrorTime.current = now;
    }
  };

  const initScanner = async () => {
    if (!Html5Qrcode.getCameras) return;
    const html5QrCode = new Html5Qrcode(qrRegionId);
    qrCodeScannerRef.current = html5QrCode;
    await startScanner(facingMode);
  };

  const startScanner = async (facingMode: CameraFacingMode) => {
    try {
      await qrCodeScannerRef.current?.start(
        {
          facingMode,
        },
        config,
        qrCodeSuccessCallback,
        qrCodeErrorCallback,
      );
      setTrackCapabilities({
        ...qrCodeScannerRef.current?.getRunningTrackCapabilities(),
      });

      const newConstraints = {
        focusMode: 'continuous',
      } as MediaTrackConstraints;
      await qrCodeScannerRef.current?.applyVideoConstraints(newConstraints);
      setMediaTrackConstraints(newConstraints);
      setIsScanning(true);
      setFacingMode(facingMode);
    } catch (err) {
      console.error('Error starting scanner:', err);
    }
  };

  const stopScanner = async () => {
    if (qrCodeScannerRef.current) {
      await qrCodeScannerRef.current.stop();
      qrCodeScannerRef.current.clear();
      setIsScanning(false);
    }
  };

  const handleToggleFacingModeClick = async () => {
    if (isScanning) {
      await stopScanner();
    }
    const mode = facingMode === 'user' ? 'environment' : 'user';
    await startScanner(mode);
  };

  useEffect(() => {
    void initScanner();
    return () => {
      void stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleTorchClick = async () => {
    const currentTorchState = mediaTrackConstraints.torch;
    const newConstraints = {
      ...mediaTrackConstraints,
      torch: !currentTorchState,
    } as MediaTrackConstraints;
    try {
      await qrCodeScannerRef.current?.applyVideoConstraints(newConstraints);
      setMediaTrackConstraints(newConstraints);
      setTrackCapabilities({
        ...qrCodeScannerRef.current?.getRunningTrackCapabilities(),
      });
    } catch (error) {
      console.error('Error toggling torch:', error);
    }
  };

  const handleZoomChange = async (val: number[]) => {
    const trackCapabilities =
      qrCodeScannerRef.current?.getRunningTrackCapabilities() as MediaTrackSettings & {
        zoom?: { max: number; min: number; step: number };
      };
    if (trackCapabilities?.zoom) {
      const desiredZoom = val[0];
      const newConstraints = {
        focusMode: 'continuous',
        zoom: desiredZoom,
      } as MediaTrackConstraints;
      try {
        await qrCodeScannerRef.current?.applyVideoConstraints(newConstraints);
        setMediaTrackConstraints(newConstraints);
        setTrackCapabilities({
          ...qrCodeScannerRef.current?.getRunningTrackCapabilities(),
        });
      } catch (error) {
        console.error('Error handle zoom:', error);
      }
    }
  };

  return (
    <div className="text-center">
      <div id={qrRegionId} className="m-auto my-4 w-full" />
      {isScanning ? (
        <div className="flex flex-col justify-center gap-4">
          {trackCapabilities?.zoom && (
            <div className="flex flex-col gap-2">
              <span className="font-medium">Zoom</span>
              <div className="flex gap-1">
                <Minus className="text-muted-foreground" />
                <Slider
                  min={trackCapabilities?.zoom.min}
                  max={trackCapabilities?.zoom.max}
                  step={trackCapabilities?.zoom.step}
                  onValueChange={(e) => void handleZoomChange(e)}
                />
                <Plus className="text-muted-foreground" />
              </div>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <Button
              variant={'outline'}
              size={'icon'}
              onClick={() => void handleToggleFacingModeClick()}
            >
              <SwitchCameraIcon />
            </Button>
            {trackCapabilities?.torch && (
              <Button
                variant={'outline'}
                size={'icon'}
                onClick={() => void handleToggleTorchClick()}
              >
                {mediaTrackConstraints?.torch ? (
                  <FlashlightOff />
                ) : (
                  <Flashlight />
                )}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default QRCodeScanner;
